import React, {useEffect} from 'react';

import {createRoute, redirect, withRouter, type AnyRoute} from 'client/routeHelpers';
import {InjectedOutlet} from 'lib/router/layoutContext';

import AssignmentsTabContent from './Assignments/Assignments';
import {clearSkillPathCache} from './SkillPath/hooks/useStudentSkillPath';
import {IndependentPractice as IndependentPracticeTabContent} from './IndependentPractice/IndependentPractice';
import {getFeatureFlagStatus} from 'client/components/FeatureFlag/featureFlag.utils';
import sessionStore from 'client/Session/SessionStore';
import {SkillPath as SkillPathTabContent} from './SkillPath/SkillPath';
import StudentHomepage from './StudentHomepage';
import TeacherDashboardGate from 'client/TeacherDashboard/TeacherDashboardGate';
import WelcomeComponent from './Welcome';

export {Routes} from './routes.constants';

/**
 * RR3 `getComponent` chooser: students get the student homepage, everyone else
 * the (gated) teacher dashboard. Re-evaluated on each render, matching RR3.
 *
 * `/home` is a LAYOUT route — its tab children (`assignments`, `skill-path`,
 * etc.) render into StudentHomepage's `{children}`. So the chosen component
 * receives `<InjectedOutlet/>` as children (StudentHomepage renders it;
 * TeacherDashboardGate ignores children and routes internally).
 *
 * The RR3 `onLeave={() => clearSkillPathCache()}` has no TanStack equivalent, so
 * it is preserved as an unmount cleanup effect here.
 */
function HomeChooser(props: Record<string, unknown>) {
  useEffect(() => () => clearSkillPathCache(), []);
  const Component = (
    sessionStore.isStudent() ? StudentHomepage : TeacherDashboardGate
  ) as React.ComponentType<Record<string, unknown>>;
  return (
    <Component {...props}>
      <InjectedOutlet />
    </Component>
  );
}

/** RR3 `onEnter` session / feature-flag redirects → async beforeLoad. */
async function homeBeforeLoad() {
  if (!sessionStore.hasValidSession()) {
    throw redirect({to: '/log-in'} as never);
  }
  if (!sessionStore.isStudent() && !sessionStore.isTeacher()) {
    throw redirect({to: '/classes'} as never);
  }
  if (!sessionStore.isStudent()) {
    // Redirect flag-off teachers in-transition so /home never flashes for them.
    const isEnabled = await getFeatureFlagStatus('teacher_dashboard_v2');
    if (!isEnabled) {
      throw redirect({to: '/classes'} as never);
    }
  }
}

export function makeHomeRoute(parentRoute: AnyRoute): AnyRoute {
  const homeRoute = createRoute({
    getParentRoute: () => parentRoute,
    path: 'home',
    component: withRouter(HomeChooser),
    beforeLoad: homeBeforeLoad
  });

  return homeRoute.addChildren([
    // (class/:classroomId/)assignments → two routes
    createRoute({
      getParentRoute: () => homeRoute,
      path: 'assignments',
      component: withRouter(AssignmentsTabContent)
    }),
    createRoute({
      getParentRoute: () => homeRoute,
      path: 'class/$classroomId/assignments',
      component: withRouter(AssignmentsTabContent)
    }),

    // (class/:classroomId/)independent-practice → two routes
    createRoute({
      getParentRoute: () => homeRoute,
      path: 'independent-practice',
      component: withRouter(IndependentPracticeTabContent)
    }),
    createRoute({
      getParentRoute: () => homeRoute,
      path: 'class/$classroomId/independent-practice',
      component: withRouter(IndependentPracticeTabContent)
    }),

    // (class/:classroomId/)skill-path → two routes
    createRoute({
      getParentRoute: () => homeRoute,
      path: 'skill-path',
      component: withRouter(SkillPathTabContent)
    }),
    createRoute({
      getParentRoute: () => homeRoute,
      path: 'class/$classroomId/skill-path',
      component: withRouter(SkillPathTabContent)
    }),

    createRoute({
      getParentRoute: () => homeRoute,
      path: 'welcome',
      component: withRouter(WelcomeComponent)
    }),

    // RR3 had no index route under `home`: bare `/home` rendered the homepage
    // with null children (its own logic then redirects to a default tab).
    // Render nothing here so the Outlet doesn't fall through to not-found.
    createRoute({getParentRoute: () => homeRoute, path: '/', component: () => null})
  ]);
}
